/** @file TileColorPanel: current swatch, hex field, the puppet's palette strip and the recent picks. */
import type { Rgba } from '@zilo/pose3d-image-io/types/image';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { tileHexOf } from './tileHexOf';
import { tilePaintUi } from './tilePaintUi';
import { tileParseHex } from './tileParseHex';

/** The palette found in the puppet's sheets (most frequent first, up to 32). */
export type TileColorPanelProps = { palette: readonly Rgba[] };

/** CSS colour of an RGBA. */
const css = (c: Rgba): string => `rgba(${String(c[0])}, ${String(c[1])}, ${String(c[2])}, ${String(c[3] / 255)})`;

/** A strip of clickable swatches; a click makes the colour current (and a recent pick). */
function Swatches({ label, colors }: { label: string; colors: readonly Rgba[] }): ReactElement {
  const pick = useStore(tilePaintUi, (s) => s.pick);
  return (
    <div className="p3-tile-swatches" role="group" aria-label={label}>
      {colors.map((c) => (
        <button key={tileHexOf(c)} type="button" className="p3-tile-swatch" title={tileHexOf(c)} aria-label={`colour ${tileHexOf(c)}`}
          onClick={() => { pick(c); }}>
          <span style={{ background: css(c) }} />
        </button>
      ))}
    </div>
  );
}

/** The colour section of the tool column; the hex field applies on Enter or when it loses focus. */
export function TileColorPanel({ palette }: TileColorPanelProps): ReactElement {
  const color = useStore(tilePaintUi, (s) => s.color);
  const recent = useStore(tilePaintUi, (s) => s.recent);
  const pick = useStore(tilePaintUi, (s) => s.pick);
  const [text, setText] = useState(tileHexOf(color));
  useEffect(() => { setText(tileHexOf(color)); }, [color]);
  const apply = (): void => {
    const parsed = tileParseHex(text);
    if (parsed === null) setText(tileHexOf(color));
    else pick(parsed);
  };
  return (
    <div className="p3-stack" style={{ gap: 6 }}>
      <div className="p3-row" style={{ gap: 6 }}>
        <span className="p3-tile-swatch p3-tile-current" data-testid="current-colour" title={tileHexOf(color)}><span style={{ background: css(color) }} /></span>
        <input className="p3-input p3-mono" aria-label="Hex colour" value={text} style={{ width: 92 }}
          onChange={(e) => { setText(e.target.value); }} onBlur={apply}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} />
      </div>
      <span className="p3-muted" style={{ fontSize: 11 }}>Palette ({palette.length})</span>
      <Swatches label="Palette" colors={palette} />
      <span className="p3-muted" style={{ fontSize: 11 }}>Recent</span>
      <Swatches label="Recent colours" colors={recent} />
    </div>
  );
}
